import React from 'react'
import { useTranslation } from 'react-i18next'
import { generatePath } from 'react-router-dom'

import { NearbyHotelCard } from './NearbyHotelCard'
import * as styles from './NearbyHotels.module.scss'

import { HotelRoomResponse } from '../../../apis/fvillage'
import { PATH } from '../../../constants'
import { useNearbyHotelsApi, usePublication } from '../../../hooks'
import { isTowerEleven } from '../../../utils'

const MAX = 3

type SearchParams = {
  checkInDate: string
  checkOutDate: string
  numberOfAdults: number
  numberOfChildren: number
  numberOfInfants: number
  roomCount: number
  // キャンペーンURL由来のクーポン。他施設の予約フローでも適用されるよう引き継ぐ。
  discountCode?: string
}

const cheapest = (
  rooms?: { minPrice: number; canReserve: boolean }[]
): number | null => {
  const prices = (rooms ?? [])
    .filter((r) => r.canReserve && r.minPrice > 0)
    .map((r) => r.minPrice)
  return prices.length ? Math.min(...prices) : null
}

const buildPath = (slug: string, p: SearchParams): string => {
  const sp = new URLSearchParams({
    check_in: p.checkInDate,
    check_out: p.checkOutDate,
    adults: String(p.numberOfAdults),
    children: String(p.numberOfChildren),
    infants: String(p.numberOfInfants),
    rooms: String(p.roomCount),
  })
  // キャンペーンURLのクーポンを引き継ぐ（他施設の予約フローでも自動適用される）。
  if (p.discountCode) {
    sp.set('discount_code', p.discountCode)
  }
  return `${generatePath(PATH.rooms, { property: slug })}?${sp.toString()}`
}

const NearbyHotelItem: React.FC<{
  hotel: HotelRoomResponse
  price: number
  to: string
}> = ({ hotel, price, to }) => {
  const { i18n } = useTranslation()
  const publication = usePublication(i18n.language, hotel.publications)
  const name = publication?.shortName ?? publication?.name ?? hotel.slug ?? ''
  return (
    <NearbyHotelCard
      name={name}
      image={hotel.images?.[0]?.url}
      minPrice={price}
      to={to}
    />
  )
}

export const NearbyHotels: React.FC<{
  property?: string
  params: SearchParams
}> = ({ property, params }) => {
  const { t } = useTranslation()
  const { data, error, isLoading } = useNearbyHotelsApi({
    checkInDate: params.checkInDate,
    checkOutDate: params.checkOutDate,
    roomCount: params.roomCount,
    numberOfAdults: params.numberOfAdults,
    numberOfChildren: params.numberOfChildren,
    numberOfInfants: params.numberOfInfants,
    limit: 10,
  })

  // `tower11_hotel` and `aos_demo` are the same physical hotel, so when the
  // current property is a T11 slug we must exclude every T11 slug from the list.
  const currentIsT11 = isTowerEleven(property)
  const isExcluded = (slug: string) =>
    currentIsT11 ? isTowerEleven(slug) : slug === property

  const hotels: Array<{ hotel: HotelRoomResponse; price: number }> = (
    !error && Array.isArray(data) ? (data as HotelRoomResponse[]) : []
  )
    .filter((h) => h.slug && !isExcluded(h.slug))
    .map((h) => ({ hotel: h, price: cheapest(h.rooms) }))
    .filter(
      (x): x is { hotel: HotelRoomResponse; price: number } => x.price != null
    )
    .slice(0, MAX)

  // 近隣検索は全 fvillage 施設を横断する重い API なので、読み込み中は
  // 黙って消える（null）のではなく検索中であることを示し、体感の遅さを和らげる。
  // エラー時は枠を出さず即 null（無駄なプレースホルダを残さない）。
  // 検索中はタイトルを出さず、中央寄せのローディング文言のみ表示する
  // （結果が出てから「近隣ホテル」見出しを出すことで上下のバランスを保つ）。
  if (isLoading && !error) {
    return (
      <div className={styles.loading}>
        <span>{t('empty.soldOutRecommend.nearbyLoading')}</span>
        <span className={styles.dots} aria-hidden>
          <span className={styles.dot} />
          <span className={styles.dot} />
          <span className={styles.dot} />
        </span>
      </div>
    )
  }

  if (hotels.length === 0) return null

  return (
    <div>
      <div className={styles.title}>
        {t('empty.soldOutRecommend.nearbyTitle')}
      </div>
      {hotels.map(({ hotel, price }) => (
        <NearbyHotelItem
          key={hotel.slug}
          hotel={hotel}
          price={price}
          to={buildPath(hotel.slug as string, params)}
        />
      ))}
    </div>
  )
}
